import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  TextField,
  Avatar,
  Chip,
  Dialog,
  DialogContent,
  Grid,
  InputLabel,
  Select,
  MenuItem,
  FormControl,
  IconButton,
  Paper,
  Skeleton,
} from '@mui/material';
import { Close as CloseIcon, AccountBoxOutlined, EditOutlined } from '@mui/icons-material';
import { Loader } from '../common/Loader';
import { BackButton } from '../common/BackButton';
import { useProfile } from '../../hooks/useProfile';
import { useAuthStore } from '../../stores/authStore';
import { useNavigate, useLocation } from 'react-router-dom';
import { getUserPhotoUrl } from '../../utils/getUserPhotoUrl';

type DialogType = 'editProfile' | 'addChild' | 'addPet' | 'editChild' | 'editPet' | null;

interface EditFormData {
  name: string;
  phone: string;
  email: string;
  branchId: string;
}

interface ChildFormData {
  name: string;
  birthDate: string;
  branchId: string;
}

interface PetFormData {
  name: string;
  birthDate: string;
  breed: string;
  branchId: string;
}

// Format date input as dd.mm.yyyy, only allow digits
const formatDateInput = (value: string): string => {
  // Remove non-numeric characters
  const cleaned = value.replace(/\D/g, '');

  // Limit to 8 digits
  const limited = cleaned.slice(0, 8);

  // Format as dd.mm.yyyy
  if (limited.length <= 2) {
    return limited;
  } else if (limited.length <= 4) {
    return limited.slice(0, 2) + '.' + limited.slice(2);
  } else {
    return limited.slice(0, 2) + '.' + limited.slice(2, 4) + '.' + limited.slice(4);
  }
};

// Convert database date format (yyyy-mm-dd) to input format (dd.mm.yyyy)
const convertDateToInputFormat = (dateString: string | undefined): string => {
  if (!dateString) return '';

  // If it's already in dd.mm.yyyy format, return as is
  if (/^\d{2}\.\d{2}\.\d{4}$/.test(dateString)) {
    return dateString;
  }

  // Parse ISO format (yyyy-mm-dd)
  const parts = dateString.split('-');
  if (parts.length === 3) {
    const [year, month, day] = parts;
    return `${day}.${month}.${year}`;
  }

  // Parse other formats if needed
  return dateString;
};

// Validate date format and values - must be complete dd.mm.yyyy with valid values
const isValidDateFormat = (dateString: string): boolean => {
  if (!dateString) return false;

  // Check format first
  if (!/^\d{2}\.\d{2}\.\d{4}$/.test(dateString)) {
    return false;
  }

  const parts = dateString.split('.');
  const day = parseInt(parts[0], 10);
  const month = parseInt(parts[1], 10);
  const year = parseInt(parts[2], 10);

  // Validate month
  if (month < 1 || month > 12) {
    return false;
  }

  // Validate day
  if (day < 1 || day > 31) {
    return false;
  }

  // More precise day validation based on month
  const daysInMonth = [31, 28, 31, 30, 31, 30, 31, 31, 30, 31, 30, 31];

  // Check for leap year
  if (month === 2 && ((year % 4 === 0 && year % 100 !== 0) || year % 400 === 0)) {
    if (day > 29) return false;
  } else if (day > daysInMonth[month - 1]) {
    return false;
  }

  // Validate year - must be reasonable (1900-2025)
  const currentYear = new Date().getFullYear();
  if (year < 1900 || year > currentYear) {
    return false;
  }

  return true;
};

export const ProfileList: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user: authUser, setUser: setAuthUser } = useAuthStore();
  const {
    profile,
    branches,
    childrenBalances,
    children,
    pets,
    features,
    isLoadingProfile,
    isLoadingBalance,
    profileError,
    handleUpdateProfile,
    handleChangeBranch,
    handleAddChild,
    handleUpdateChild,
    handleDeleteChild,
    handleAddPet,
    handleUpdatePet,
    handleDeletePet,
    refreshAllData,
    refreshProfile,
  } = useProfile();

  // Calculate currentBranchId at the top of the component
  const currentBranchId = authUser?.branchId || profile?.branchId || '';

  // Dialog state
  const [openDialog, setOpenDialog] = useState<DialogType>(null);
  const [editingChildId, setEditingChildId] = useState<string | null>(null);
  const [editingPetId, setEditingPetId] = useState<string | null>(null);

  // Edit profile form
  const [editFormData, setEditFormData] = useState<EditFormData>({
    name: '',
    phone: '',
    email: '',
    branchId: currentBranchId,
  });

  // Child form
  const [childFormData, setChildFormData] = useState<ChildFormData>({
    name: '',
    birthDate: '',
    branchId: currentBranchId,
  });

  // Pet form
  const [petFormData, setPetFormData] = useState<PetFormData>({
    name: '',
    birthDate: '',
    breed: '',
    branchId: currentBranchId,
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [userPhotoUrl, setUserPhotoUrl] = useState<string | null>(null);

  useEffect(() => {
    const photoUrl = getUserPhotoUrl();
    setUserPhotoUrl(photoUrl);
  }, []);

  useEffect(() => {
    if (profile && openDialog === 'editProfile') {
      setEditFormData({
        name: profile.name || '',
        phone: profile.phone || '',
        email: profile.email || '',
        branchId: profile.branchId || currentBranchId,
      });
    }
  }, [profile, openDialog, currentBranchId]);

  const handleEditProfileClick = () => {
    // Заполняем форму текущими данными профиля
    setEditFormData({
      name: profile?.name || '',
      phone: profile?.phone || '',
      email: profile?.email || '',
      branchId: profile?.branchId || currentBranchId,
    });
    setOpenDialog('editProfile');
  };

  const handleSaveProfile = async () => {
    setIsSubmitting(true);
    try {
      await handleUpdateProfile({ name: editFormData.name });

      if (editFormData.branchId && editFormData.branchId !== profile?.branchId) {
        await handleChangeBranch(editFormData.branchId);

        // Обновляем филиал всех детей
        for (const child of children) {
          await handleUpdateChild(child.id, {
            name: child.name,
            birthDate: child.birthDate,
            branchId: editFormData.branchId,
          });
        }

        // Обновляем филиал всех питомцев
        for (const pet of pets) {
          await handleUpdatePet(pet.id, {
            name: pet.name,
            breed: pet.breed,
            birthDate: pet.birthDate,
            branchId: editFormData.branchId,
          });
        }
      }

      // Перезагружаем данные БЕЗ профиля (дети, питомцы, филиалы, баланс)
      await refreshAllData();

      // Отдельно получаем обновленный профиль с задержкой для гарантии синхронизации
      await new Promise(resolve => setTimeout(resolve, 500));
      await refreshProfile();

      // Обновляем authStore с новым филиалом
      if (authUser && editFormData.branchId) {
        setAuthUser({ ...authUser, branchId: editFormData.branchId });
      }

      setOpenDialog(null);
    } catch (error) {
      console.error('Ошибка при сохранении профиля:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleEditChildClick = (child: any) => {
    setEditingChildId(child.id);
    setChildFormData({
      name: child.name,
      birthDate: convertDateToInputFormat(child.birthDate),
      branchId: child.branchId || currentBranchId,
    });
    setOpenDialog('editChild');
  };

  const handleEditPetClick = (pet: any) => {
    setEditingPetId(pet.id);
    setPetFormData({
      name: pet.name,
      breed: pet.breed || '',
      birthDate: convertDateToInputFormat(pet.birthDate),
      branchId: pet.branchId || currentBranchId,
    });
    setOpenDialog('editPet');
  };

  const handleAddChildSubmit = async () => {
    if (!childFormData.name || !childFormData.birthDate) return;

    setIsSubmitting(true);
    try {
      const result = await handleAddChild({
        name: childFormData.name,
        birthDate: childFormData.birthDate,
        branchId: currentBranchId,
      });
      if (result.success) {
        setOpenDialog(null);
        setChildFormData({ name: '', birthDate: '', branchId: '' });
      } else {
        console.error('Ошибка при добавлении ребенка:', result.error);
      }
    } catch (error) {
      console.error('Ошибка при добавлении ребенка:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleAddPetSubmit = async () => {
    if (!petFormData.name || !petFormData.birthDate) return;

    setIsSubmitting(true);
    try {
      const result = await handleAddPet({
        name: petFormData.name,
        breed: petFormData.breed || undefined,
        birthDate: petFormData.birthDate,
        branchId: currentBranchId,
      });
      if (result.success) {
        setOpenDialog(null);
        setPetFormData({ name: '', birthDate: '', breed: '', branchId: '' });
      } else {
        console.error('Ошибка при добавлении питомца:', result.error);
      }
    } catch (error) {
      console.error('Ошибка при добавлении питомца:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleUpdateChildSubmit = async () => {
    if (!editingChildId || !childFormData.name || !childFormData.birthDate) return;

    setIsSubmitting(true);
    try {
      const result = await handleUpdateChild(editingChildId, {
        name: childFormData.name,
        birthDate: childFormData.birthDate,
      });
      if (result.success) {
        setOpenDialog(null);
        setEditingChildId(null);
        setChildFormData({ name: '', birthDate: '', branchId: '' });
      } else {
        console.error('Ошибка при обновлении ребенка:', result.error);
      }
    } catch (error) {
      console.error('Ошибка при обновлении ребенка:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleUpdatePetSubmit = async () => {
    if (!editingPetId || !petFormData.name || !petFormData.birthDate) return;

    setIsSubmitting(true);
    try {
      const result = await handleUpdatePet(editingPetId, {
        name: petFormData.name,
        breed: petFormData.breed || undefined,
        birthDate: petFormData.birthDate,
      });
      if (result.success) {
        setOpenDialog(null);
        setEditingPetId(null);
        setPetFormData({ name: '', birthDate: '', breed: '', branchId: '' });
      } else {
        console.error('Ошибка при обновлении питомца:', result.error);
      }
    } catch (error) {
      console.error('Ошибка при обновлении питомца:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  if (profileError && !profile) {
    return (
      <Paper sx={{ p: 3, bgcolor: '#ffebee', borderRadius: 2, textAlign: 'center' }}>
        <Typography color="error">Ошибка: {profileError}</Typography>
      </Paper>
    );
  }

  return (
    <Box sx={{ bgcolor: '#F2F3F5', minHeight: '100vh', pb: 12 }}>
      <Box sx={{ pt: 1.5 }}>
      <BackButton onClick={() => navigate(-1)} />

        {/* Page Title */}
        <Typography
          sx={{
            fontSize: { xs: 24, sm: 28, md: 32 },
            fontWeight: 'bold',
            color: '#1C1C1E',
            mb: 2,
          }}
        >
          Мои данные
        </Typography>

        {/* Profile Card */}
        <Paper
          sx={{
            p: 2,
            mb: 2,
            borderRadius: '16px',
            display: 'flex',
            alignItems: 'center',
            gap: 1.5,
          }}
        >
          <Avatar
            src={userPhotoUrl || undefined}
            sx={{
              width: { xs: 44, sm: 48 },
              height: { xs: 44, sm: 48 },
              bgcolor: '#1976FF',
              fontSize: { xs: 18, sm: 20 },
              fontWeight: 600,
            }}
          >
            {profile?.name?.charAt(0) || '?'}
          </Avatar>
          <Box sx={{ flex: 1 }}>
            {isLoadingProfile ? (
              <>
                <Skeleton variant="text" width="60%" sx={{ mb: 0.5 }} />
                <Skeleton variant="text" width="40%" />
              </>
            ) : (
              <>
                <Typography sx={{ fontSize: { xs: 15, sm: 16 }, fontWeight: 600, color: '#1C1C1E' }}>
                  {profile?.name || 'Пользователь'}
                </Typography>
                <Typography sx={{ fontSize: '13px', color: '#8E8E93' }}>
                  {branches.find(b => b.id === (authUser?.branchId || profile?.branchId))?.name || 'Филиал не указан'}
                </Typography>
              </>
            )}
          </Box>
          <Box
            sx={{
              width: { xs: 36, sm: 40 },
              height: { xs: 36, sm: 40 },
              bgcolor: '#1976FF',
              color: 'white',
              borderRadius: '50%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <AccountBoxOutlined sx={{ fontSize: { xs: 18, sm: 20 } }} />
          </Box>
        </Paper>

        {/* Personal Data Block */}
        <Box sx={{ display: 'flex', justifyContent: 'center', width: '100%', mb: 2 }}>
          <Paper sx={{ borderRadius: '16px', p: 2, width: '100%', maxWidth: { xs: '100%', sm: '100%', md: '720px', lg: '900px' } }}>
          {/* Header */}
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Typography
              sx={{
                fontSize: { xs: 20, sm: 24 },
                fontWeight: 600,
                color: '#1C1C1E',
                fontFamily: 'TikTok Sans, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
              }}
            >
              Личные данные
            </Typography>
            <Button
              size="small"
              onClick={handleEditProfileClick}
              sx={{
                textTransform: 'none',
                color: '#1976FF',
                fontSize: '14px',
                fontWeight: 600,
                p: 0,
                gap: 0.5,
                '&:hover': { bgcolor: 'transparent' },
              }}
            >
          Изменить 
          <EditOutlined 
          sx={{
                fontSize: 16, 
                position: 'relative',
                top: '1px',
            }}
            />
            </Button>
          </Box>

          {/* Content */}
          <Box>
            {/* Name - Full width */}
            <Box sx={{ mb: 2 }}>
              <Typography
                sx={{
                  fontSize: { xs: 12, sm: 13 },
                  color: '#8E8E93',
                  mb: 0.5,
                  fontFamily: 'TikTok Sans, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
                  fontWeight: 500,
                  lineHeight: 1,
                }}
              >
                Имя
              </Typography>
              <Typography
                sx={{
                  fontSize: { xs: 14, sm: 16 },
                  color: '#1C1C1E',
                  fontFamily: 'TikTok Sans, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
                  fontWeight: 500,
                }}
              >
                {profile?.name || 'Не указано'}
              </Typography>
            </Box>

            {/* Phone and Email - Same row */}
            <Grid container spacing={2}>
              <Grid item xs={6}>
                <Box>
                  <Typography
                    sx={{
                      fontSize: { xs: 12, sm: 13 },
                      color: '#8E8E93',
                      mb: 0.5,
                      fontFamily: 'TikTok Sans, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
                      fontWeight: 500,
                      lineHeight: 1,
                    }}
                  >
                    Телефон
                  </Typography>
                  <Typography
                    sx={{
                      fontSize: { xs: 14, sm: 16 },
                      color: '#1C1C1E',
                      fontFamily: 'TikTok Sans, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
                      fontWeight: 500,
                    }}
                  >
                    {profile?.phone || 'Не указано'}
                  </Typography>
                </Box>
              </Grid>

              <Grid item xs={6}>
                <Box>
                  <Typography
                    sx={{
                      fontSize: { xs: 12, sm: 13 },
                      color: '#8E8E93',
                      mb: 0.5,
                      fontFamily: 'TikTok Sans, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
                      fontWeight: 500,
                      lineHeight: 1,
                    }}
                  >
                    Email
                  </Typography>
                  <Typography
                    sx={{
                      fontSize: { xs: 14, sm: 16 },
                      color: '#1C1C1E',
                      fontFamily: 'TikTok Sans, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
                      fontWeight: 500,
                    }}
                  >
                    {profile?.email || 'Не заполнено'}
                  </Typography>
                </Box>
              </Grid>
            </Grid>

            {/* Branches */}
            {branches.length > 0 && (
              <Box sx={{ mt: 2.5 }}>
                <Typography
                  sx={{
                    fontSize: { xs: 12, sm: 13 },
                    color: '#8E8E93',
                    mb: 2,
                    fontFamily: 'TikTok Sans, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
                    fontWeight: 500,
                    lineHeight: 1,
                  }}
                >
                  Филиал
                </Typography>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                  {branches.map((branch) => (
                    <Chip
                      key={branch.id}
                      label={branch.name}
                      sx={{
                        borderRadius: '16px',
                        height: '32px',
                        fontSize: '13px',
                        fontFamily: 'TikTok Sans, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
                        fontWeight: 600,
                        bgcolor: currentBranchId === branch.id ? '#1976FF' : '#FFFFFF',
                        color: currentBranchId === branch.id ? '#FFFFFF' : '#1C1C1E',
                        border: currentBranchId === branch.id ? 'none' : '1px solid #D1D1D6',
                        cursor: 'default',
                      }}
                    />
                  ))}
                </Box>
              </Box>
            )}
          </Box>
        </Paper>
        </Box>

        {/* Children Cards Block */}
        {features.addChild && (
          <>
            {/* Skeleton Loader for Children */}
            {isLoadingBalance && children.length === 0 && (
              <Paper
                sx={{
                  p: 2,
                  mb: 2,
                  borderRadius: '16px',
                  border: '1px solid #E5E5EA',
                  width: '100%',
                  maxWidth: { xs: '100%', sm: '359px', md: '480px', lg: '600px' },
                }}
              >
                <Box sx={{ mb: 2 }}>
                  <Skeleton variant="text" width="40%" sx={{ mb: 1 }} />
                  <Skeleton variant="text" width="60%" />
                </Box>
                <Skeleton variant="rectangular" height={60} sx={{ borderRadius: 1 }} />
              </Paper>
            )}
            {/* Children Cards */}
            {children.length > 0 &&
              children.map((child) => (
                <Paper
                  key={child.id}
                  sx={{
                    p: 2,
                    mb: 2,
                    borderRadius: '16px',
                    border: '1px solid #E5E5EA',
                    width: '100%',
                    maxWidth: { xs: '100%', sm: '359px', md: '480px', lg: '600px' },
                  }}
                >
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                    <Typography
                      sx={{
                        fontSize: { xs: 20, sm: 24 },
                        fontWeight: 600,
                        color: '#1C1C1E',
                        fontFamily: 'TikTok Sans, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
                      }}
                    >
                      Данные ребёнка
                    </Typography>
                    <Button
                      size="small"
                      onClick={() => handleEditChildClick(child)}
                      sx={{
                        textTransform: 'none',
                        color: '#1976FF',
                        fontSize: '14px',
                        fontWeight: 600,
                        p: 0,
                        minWidth: 'auto',
                        gap: 0.5,
                      }}
                    >
                      Изменить 
                    <EditOutlined 
                    sx={{
                          fontSize: 16,
                          position: 'relative',
                          top: '1px',
                      }}
                      />
                    </Button>
                  </Box>

                  <Grid container spacing={2}>
                    <Grid item xs={6}>
                      <Box>
                        <Typography
                          sx={{
                            fontSize: { xs: 12, sm: 13 },
                            color: '#8E8E93',
                            mb: 0.5,
                            fontFamily: 'TikTok Sans, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
                            fontWeight: 500,
                            lineHeight: 1,
                          }}
                        >
                          Имя
                        </Typography>
                        <Typography
                          sx={{
                            fontSize: { xs: 14, sm: 16 },
                            color: '#1C1C1E',
                            fontFamily: 'TikTok Sans, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
                            fontWeight: 500,
                          }}
                        >
                          {child.name}
                        </Typography>
                      </Box>
                    </Grid>

                    <Grid item xs={6}>
                      <Box>
                        <Typography
                          sx={{
                            fontSize: { xs: 12, sm: 13 },
                            color: '#8E8E93',
                            mb: 0.5,
                            fontFamily: 'TikTok Sans, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
                            fontWeight: 500,
                            lineHeight: 1,
                          }}
                        >
                          Дата рождения
                        </Typography>
                        <Typography
                          sx={{
                            fontSize: { xs: 14, sm: 16 },
                            color: '#1C1C1E',
                            fontFamily: 'TikTok Sans, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
                            fontWeight: 500,
                          }}
                        >
                          {child.birthDate ? new Date(child.birthDate).toLocaleDateString('ru-RU') : 'Не указано'}
                        </Typography>
                      </Box>
                    </Grid>

                    {/* Филиал */}
                    {branches.length > 0 && (
                      <Grid item xs={12}>
                        <Box>
                          <Typography
                            sx={{
                              fontSize: { xs: 12, sm: 13 },
                              color: '#8E8E93',
                              mb: 2,
                              fontFamily: 'TikTok Sans, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
                              fontWeight: 500,
                              lineHeight: 1,
                            }}
                          >
                            Филиал
                          </Typography>
                          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                            {branches.map((branch) => (
                              <Chip
                                key={branch.id}
                                label={branch.name}
                                sx={{
                                  borderRadius: '16px',
                                  height: '32px',
                                  fontSize: { xs: 12, sm: 13 },
                                  fontFamily: 'TikTok Sans, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
                                  fontWeight: 600,
                                  bgcolor: branch.id === child.branchId ? '#1976FF' : '#FFFFFF',
                                  color: branch.id === child.branchId ? '#FFFFFF' : '#1C1C1E',
                                  border: branch.id === child.branchId ? 'none' : '1px solid #D1D1D6',
                                  cursor: 'default',
                                }}
                              />
                            ))}
                          </Box>
                        </Box>
                      </Grid>
                    )}
                  </Grid>
                </Paper>
              ))}

            {/* Add Children Button Block */}
            <Paper sx={{ borderRadius: '16px', p: 2, mb: 2, width: '100%', maxWidth: { xs: '100%', sm: '359px', md: '480px', lg: '600px' }, margin: '0 auto 32px auto' }}>
              <Typography sx={{
                fontSize: { xs: 20, sm: 24 },
                fontWeight: 600,
                color: '#1C1C1E',
                fontFamily: 'TikTok Sans, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
                pb: '24px'
              }}>
                Карточки детей
              </Typography>

              <Button
                fullWidth
                variant="contained"
                sx={{
                  bgcolor: '#1976FF',
                  color: '#FFFFFF',
                  textTransform: 'none',
                  borderRadius: '24px',
                  height: { xs: 48, sm: 52 },
                  fontSize: { xs: 14, sm: 15 },
                  fontWeight: 600,
                  '&:hover': { bgcolor: '#1565E0' },
                }}
                onClick={() => setOpenDialog('addChild')}
              >
                Добавить карточку
              </Button>
            </Paper>
          </>
        )}

        {/* Pet Cards Block */}
        {features.pets && (
          <Box>
            {/* Skeleton Loader for Pets */}
            {isLoadingBalance && pets.length === 0 && (
              <Paper
                sx={{
                  p: 2,
                  mb: 2,
                  borderRadius: '16px',
                  border: '1px solid #E5E5EA',
                  width: '100%',
                  maxWidth: { xs: '100%', sm: '359px', md: '480px', lg: '600px' },
                }}
              >
                <Box sx={{ mb: 2 }}>
                  <Skeleton variant="text" width="40%" sx={{ mb: 1 }} />
                  <Skeleton variant="text" width="60%" />
                </Box>
                <Skeleton variant="rectangular" height={60} sx={{ borderRadius: 1 }} />
              </Paper>
            )}

            {/* Pets Cards */}
            {pets.length > 0 &&
              pets.map((pet) => (
                <Paper
                  key={pet.id}
                  sx={{
                    p: 2,
                    mb: 2,
                    borderRadius: '16px',
                    border: '1px solid #E5E5EA',
                    width: '100%',
                    maxWidth: { xs: '100%', sm: '359px', md: '480px', lg: '600px' },
                  }}
                >
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                    <Typography
                      sx={{
                        fontSize: { xs: 20, sm: 24 },
                        fontWeight: 600,
                        color: '#1C1C1E',
                        fontFamily: 'TikTok Sans, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
                      }}
                    >
                      Данные питомца
                    </Typography>
                    <Button
                      size="small"
                      onClick={() => handleEditPetClick(pet)}
                      sx={{
                        textTransform: 'none',
                        color: '#1976FF',
                        fontSize: '14px',
                        fontWeight: 600,
                        p: 0,
                        minWidth: 'auto',
                        gap: 0.5,
                      }}
                    >
                      Изменить
                     <EditOutlined 
                      sx={{
                            fontSize: 16,
                            position: 'relative',
                            top: '1px',
                        }}
                      />
                    </Button>
                  </Box>

                  <Grid container spacing={2}>
                    <Grid item xs={6}>
                      <Box>
                        <Typography
                          sx={{
                            fontSize: '13px',
                            color: '#8E8E93',
                            mb: 0.5,
                            fontFamily: 'TikTok Sans, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
                            fontWeight: 500,
                            lineHeight: 1,
                          }}
                        >
                          Имя
                        </Typography>
                        <Typography
                          sx={{
                            fontSize: '16px',
                            color: '#1C1C1E',
                            fontFamily: 'TikTok Sans, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
                            fontWeight: 500,
                          }}
                        >
                          {pet.name}
                        </Typography>
                      </Box>
                    </Grid>

                    <Grid item xs={6}>
                      <Box>
                        <Typography
                          sx={{
                            fontSize: '13px',
                            color: '#8E8E93',
                            mb: 0.5,
                            fontFamily: 'TikTok Sans, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
                            fontWeight: 500,
                            lineHeight: 1,
                          }}
                        >
                          Дата рождения
                        </Typography>
                        <Typography
                          sx={{
                            fontSize: '16px',
                            color: '#1C1C1E',
                            fontFamily: 'TikTok Sans, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
                            fontWeight: 500,
                          }}
                        >
                          {pet.birthDate ? new Date(pet.birthDate).toLocaleDateString('ru-RU') : 'Не указано'}
                        </Typography>
                      </Box>
                    </Grid>

                    {/* Порода */}
                    <Grid item xs={6}>
                      <Box>
                        <Typography
                          sx={{
                            fontSize: { xs: 12, sm: 13 },
                            color: '#8E8E93',
                            mb: 0.5,
                            fontFamily: 'TikTok Sans, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
                            fontWeight: 500,
                            lineHeight: 1,
                          }}
                        >
                          Порода
                        </Typography>
                        <Typography
                          sx={{
                            fontSize: { xs: 14, sm: 16 },
                            color: '#1C1C1E',
                            fontFamily: 'TikTok Sans, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
                            fontWeight: 500,
                          }}
                        >
                          {pet.breed || 'Не указано'}
                        </Typography>
                      </Box>
                    </Grid>

                    {/* Филиал */}
                    {branches.length > 0 && (
                      <Grid item xs={12}>
                        <Box>
                          <Typography
                            sx={{
                              fontSize: { xs: 12, sm: 13 },
                              color: '#8E8E93',
                              mb: 2,
                              fontFamily: 'TikTok Sans, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
                              fontWeight: 500,
                              lineHeight: 1,
                            }}
                          >
                            Филиал
                          </Typography>
                          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                            {branches.map((branch) => (
                              <Chip
                                key={branch.id}
                                label={branch.name}
                                sx={{
                                  borderRadius: '16px',
                                  height: '32px',
                                  fontSize: { xs: 12, sm: 13 },
                                  fontFamily: 'TikTok Sans, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
                                  fontWeight: 600,
                                  bgcolor: branch.id === pet.branchId ? '#1976FF' : '#FFFFFF',
                                  color: branch.id === pet.branchId ? '#FFFFFF' : '#1C1C1E',
                                  border: branch.id === pet.branchId ? 'none' : '1px solid #D1D1D6',
                                  cursor: 'default',
                                }}
                              />
                            ))}
                          </Box>
                        </Box>
                      </Grid>
                    )}
                  </Grid>
                </Paper>
              ))}

            {/* Add Pets Button Block */}
            <Paper sx={{ borderRadius: '16px', p: 2, mb: 4, width: '100%', maxWidth: { xs: '100%', sm: '359px', md: '480px', lg: '600px' }, margin: '0 auto 32px auto' }}>
              <Typography sx={{
                fontSize: { xs: 20, sm: 24 },
                fontWeight: 600,
                color: '#1C1C1E',
                fontFamily: 'TikTok Sans, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
                pb: '24px',
              }}>
                Карточки питомцев
              </Typography>

              <Button
                fullWidth
                variant="contained"
                sx={{
                  bgcolor: '#1976FF',
                  color: '#FFFFFF',
                  textTransform: 'none',
                  borderRadius: '24px',
                  height: { xs: 48, sm: 52 },
                  fontSize: { xs: 14, sm: 15 },
                  fontWeight: 600,
                  '&:hover': { bgcolor: '#1565E0' },
                }}
                onClick={() => setOpenDialog('addPet')}
              >
                Добавить карточку
              </Button>
            </Paper>
          </Box>
        )}
      </Box>

      {/* Edit Profile Dialog */}
      <Dialog
        open={openDialog === 'editProfile'}
        onClose={() => setOpenDialog(null)}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: '20px',
            maxHeight: '90vh',
          },
        }}
      >
        <DialogContent sx={{ p: 2 }}>
          {/* Header */}
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Avatar
                src={userPhotoUrl || undefined}
                sx={{
                  width: { xs: 44, sm: 48 },
                  height: { xs: 44, sm: 48 },
                  bgcolor: '#1976FF',
                  fontSize: { xs: 16, sm: 18 },
                  fontWeight: 600,
                }}
              >
                {profile?.name?.charAt(0) || '?'}
              </Avatar>
              <Box>
                <Typography sx={{ fontSize: { xs: 13, sm: 14 }, fontWeight: 600, color: '#1C1C1E' }}>
                  {profile?.name}
                </Typography>
                <Typography sx={{ fontSize: { xs: 11, sm: 12 }, color: '#8E8E93' }}>
                  {branches.length > 0 ? branches[0].name : ''}
                </Typography>
              </Box>
            </Box>
            <IconButton size="small" onClick={() => setOpenDialog(null)}>
              <CloseIcon />
            </IconButton>
          </Box>

          {/* Form Fields */}
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
            <Box>
              <InputLabel
                sx={{
                  color: '#007AFF',
                  fontSize: { xs: 13, sm: 14 },
                  fontWeight: 600,
                  mb: 0.75,
                  fontFamily: 'TikTok Sans, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
                  lineHeight: 1,
                }}
              >
                Введите имя
              </InputLabel>
              <TextField
                fullWidth
                value={editFormData.name}
                onChange={(e) =>
                  setEditFormData({ ...editFormData, name: e.target.value })
                }
                variant="outlined"
                size="small"
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: '16px',
                    height: '44px',
                    bgcolor: '#F9F9F9',
                  },
                  '& .MuiOutlinedInput-input': {
                    fontSize: '15px',
                    fontWeight: 600,
                    fontFamily: 'TikTok Sans, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
                    lineHeight: '1.2',
                  },
                }}
              />
            </Box>

            <Box>
              <InputLabel
                sx={{
                  color: '#007AFF',
                  fontSize: { xs: 13, sm: 14 },
                  fontWeight: 600,
                  mb: 0.75,
                  fontFamily: 'TikTok Sans, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
                  lineHeight: 1,
                }}
              >
                Введите номер телефона
              </InputLabel>
              <TextField
                fullWidth
                value={editFormData.phone}
                onChange={(e) =>
                  setEditFormData({ ...editFormData, phone: e.target.value })
                }
                variant="outlined"
                size="small"
                placeholder="+7XXXXXXXXXX"
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: '16px',
                    height: '44px',
                    bgcolor: '#F9F9F9',
                  },
                  '& .MuiOutlinedInput-input': {
                    fontSize: '15px',
                    fontWeight: 600,
                    fontFamily: 'TikTok Sans, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
                    lineHeight: '1.2',
                  },
                }}
              />
            </Box>

            <Box>
              <InputLabel
                sx={{
                  color: '#007AFF',
                  fontSize: { xs: 13, sm: 14 },
                  fontWeight: 600,
                  mb: 0.75,
                  fontFamily: 'TikTok Sans, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
                  lineHeight: 1,
                }}
              >
                Введите Email
              </InputLabel>
              <TextField
                fullWidth
                value={editFormData.email}
                onChange={(e) =>
                  setEditFormData({ ...editFormData, email: e.target.value })
                }
                variant="outlined"
                size="small"
                placeholder="example@mail.com"
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: '16px',
                    height: '44px',
                    bgcolor: '#F9F9F9',
                  },
                  '& .MuiOutlinedInput-input': {
                    fontSize: '15px',
                    fontWeight: 600,
                    fontFamily: 'TikTok Sans, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
                    lineHeight: '1.2',
                  },
                }}
              />
            </Box>

            {branches.length > 1 && (
              <Box>
                <InputLabel
                  sx={{
                    color: '#1976FF',
                    fontSize: { xs: 12, sm: 13 },
                    fontWeight: 600,
                    mb: 0.75,
                  }}
                >
                  Выбрать филиал
                </InputLabel>
                <FormControl fullWidth size="small">
                  <Select
                    value={editFormData.branchId}
                    onChange={(e) =>
                      setEditFormData({
                        ...editFormData,
                        branchId: e.target.value,
                      })
                    }
                    sx={{
                      borderRadius: '12px',
                      bgcolor: '#F9F9F9',
                      height: '44px',
                    }}
                  >
                    {branches.map((branch) => (
                      <MenuItem key={branch.id} value={branch.id}>
                        {branch.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Box>
            )}

            <Button
              fullWidth
              variant="contained"
              onClick={handleSaveProfile}
              disabled={isSubmitting || !editFormData.name}
              sx={{
                bgcolor: '#1976FF',
                color: '#FFFFFF',
                textTransform: 'none',
                borderRadius: '24px',
                height: '48px',
                fontSize: '15px',
                fontWeight: 600,
                mt: 2,
                '&:hover': { bgcolor: '#1565E0' },
              }}
            >
              {isSubmitting ? 'Сохранение...' : 'Сохранить'}
            </Button>
          </Box>
        </DialogContent>
      </Dialog>

      {/* Add Child Dialog */}
      {features.addChild && (
        <Dialog
          open={openDialog === 'addChild'}
          onClose={() => setOpenDialog(null)}
          maxWidth="sm"
          fullWidth
          PaperProps={{
            sx: {
              borderRadius: '20px',
              maxHeight: '90vh',
            },
          }}
        >
          <DialogContent sx={{ p: 2 }}>
            {/* Header */}
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
              <Typography sx={{ fontSize: { xs: 15, sm: 16 }, fontWeight: 600, color: '#1C1C1E' }}>
                Добавить ребенка
              </Typography>
              <IconButton size="small" onClick={() => setOpenDialog(null)}>
                <CloseIcon />
              </IconButton>
            </Box>

            {/* Form Fields */}
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
              <Box>
                <InputLabel
                  sx={{
                    color: '#007AFF',
                    fontSize: '14px',
                    fontWeight: 600,
                    mb: 0.75,
                    fontFamily: 'TikTok Sans, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
                    lineHeight: 1,
                  }}
                >
                  Введите имя ребенка
                </InputLabel>
                <TextField
                  fullWidth
                  value={childFormData.name}
                  onChange={(e) =>
                    setChildFormData({ ...childFormData, name: e.target.value })
                  }
                  variant="outlined"
                  size="small"
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: '16px',
                      height: '44px',
                      bgcolor: '#FFFFFF',
                      '& fieldset': {
                        borderColor: '#212121',
                        borderWidth: '1.5px',
                      },
                      '&:hover fieldset': {
                        borderColor: '#212121',
                      },
                      '&.Mui-focused fieldset': {
                        borderColor: '#007AFF',
                        borderWidth: '1.5px',
                      },
                    },
                    '& .MuiOutlinedInput-input': {
                      fontSize: { xs: 14, sm: 15 },
                      fontWeight: 600,
                      fontFamily: 'TikTok Sans, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
                      lineHeight: '1.2',
                    },
                  }}
                />
              </Box>

              <Box>
                <InputLabel
                  sx={{
                    color: '#007AFF',
                    fontSize: '14px',
                    fontWeight: 600,
                    mb: 0.75,
                    fontFamily: 'TikTok Sans, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
                    lineHeight: 1,
                  }}
                >
                  Дата рождения ребенка
                </InputLabel>
                <TextField
                  fullWidth
                  value={childFormData.birthDate}
                  onChange={(e) =>
                    setChildFormData({
                      ...childFormData,
                      birthDate: formatDateInput(e.target.value),
                    })
                  }
                  variant="outlined"
                  size="small"
                  placeholder="DD.MM.YYYY"
                  error={childFormData.birthDate.length > 0 && !isValidDateFormat(childFormData.birthDate)}
                  helperText={childFormData.birthDate.length > 0 && !isValidDateFormat(childFormData.birthDate) ? 'Введите полную дату в формате ДД.ММ.ГГГГ' : ''}
                  inputProps={{
                    maxLength: 10,
                  }}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: '16px',
                      height: '44px',
                      bgcolor: '#FFFFFF',
                      '& fieldset': {
                        borderColor: '#212121',
                        borderWidth: '1.5px',
                      },
                      '&:hover fieldset': {
                        borderColor: '#212121',
                      },
                      '&.Mui-focused fieldset': {
                        borderColor: '#007AFF',
                        borderWidth: '1.5px',
                      },
                    },
                    '& .MuiOutlinedInput-input': {
                      fontSize: { xs: 14, sm: 15 },
                      fontWeight: 600,
                      fontFamily: 'TikTok Sans, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
                      lineHeight: '1.2',
                    },
                  }}
                />
              </Box>

              <Button
                fullWidth
                variant="contained"
                onClick={handleAddChildSubmit}
                disabled={isSubmitting || !childFormData.name || !isValidDateFormat(childFormData.birthDate)}
                sx={{
                  bgcolor: '#1976FF',
                  color: '#FFFFFF',
                  textTransform: 'none',
                  borderRadius: '24px',
                  height: { xs: 44, sm: 48 },
                  fontSize: { xs: 14, sm: 15 },
                  fontWeight: 600,
                  mt: 2,
                  '&:hover': { bgcolor: '#1565E0' },
                }}
              >
                {isSubmitting ? 'Добавление...' : 'Добавить'}
              </Button>
            </Box>
          </DialogContent>
        </Dialog>
      )}

      {/* Edit Child Dialog */}
      {features.addChild && (
        <Dialog
          open={openDialog === 'editChild'}
          onClose={() => {
            setOpenDialog(null);
            setEditingChildId(null);
          }}
          maxWidth="sm"
          fullWidth
          PaperProps={{
            sx: {
              borderRadius: '20px',
              maxHeight: '90vh',
            },
          }}
        >
          <DialogContent sx={{ p: 2 }}>
            {/* Header */}
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
              <Typography sx={{ fontSize: { xs: 15, sm: 16 }, fontWeight: 600, color: '#1C1C1E' }}>
                Изменить данные ребенка
              </Typography>
              <IconButton
                size="small"
                onClick={() => {
                  setOpenDialog(null);
                  setEditingChildId(null);
                }}
              >
                <CloseIcon />
              </IconButton>
            </Box>

            {/* Form Fields */}
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
              <Box>
                <InputLabel
                  sx={{
                    color: '#007AFF',
                    fontSize: '14px',
                    fontWeight: 600,
                    mb: 0.75,
                    fontFamily: 'TikTok Sans, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
                    lineHeight: 1,
                  }}
                >
                  Введите имя ребенка
                </InputLabel>
                <TextField
                  fullWidth
                  value={childFormData.name}
                  onChange={(e) =>
                    setChildFormData({ ...childFormData, name: e.target.value })
                  }
                  variant="outlined"
                  size="small"
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: '16px',
                      height: '44px',
                      bgcolor: '#FFFFFF',
                      '& fieldset': {
                        borderColor: '#212121',
                        borderWidth: '1.5px',
                      },
                      '&:hover fieldset': {
                        borderColor: '#212121',
                      },
                      '&.Mui-focused fieldset': {
                        borderColor: '#007AFF',
                        borderWidth: '1.5px',
                      },
                    },
                    '& .MuiOutlinedInput-input': {
                      fontSize: { xs: 14, sm: 15 },
                      fontWeight: 600,
                      fontFamily: 'TikTok Sans, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
                      lineHeight: '1.2',
                    },
                  }}
                />
              </Box>

              <Box>
                <InputLabel
                  sx={{
                    color: '#007AFF',
                    fontSize: '14px',
                    fontWeight: 600,
                    mb: 0.75,
                    fontFamily: 'TikTok Sans, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
                    lineHeight: 1,
                  }}
                >
                  Дата рождения ребенка
                </InputLabel>
                <TextField
                  fullWidth
                  value={childFormData.birthDate}
                  onChange={(e) =>
                    setChildFormData({
                      ...childFormData,
                      birthDate: formatDateInput(e.target.value),
                    })
                  }
                  variant="outlined"
                  size="small"
                  placeholder="DD.MM.YYYY"
                  error={childFormData.birthDate.length > 0 && !isValidDateFormat(childFormData.birthDate)}
                  helperText={childFormData.birthDate.length > 0 && !isValidDateFormat(childFormData.birthDate) ? 'Введите полную дату в формате ДД.ММ.ГГГГ' : ''}
                  inputProps={{
                    maxLength: 10,
                  }}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: '16px',
                      height: '44px',
                      bgcolor: '#FFFFFF',
                      '& fieldset': {
                        borderColor: '#212121',
                        borderWidth: '1.5px',
                      },
                      '&:hover fieldset': {
                        borderColor: '#212121',
                      },
                      '&.Mui-focused fieldset': {
                        borderColor: '#007AFF',
                        borderWidth: '1.5px',
                      },
                    },
                    '& .MuiOutlinedInput-input': {
                      fontSize: { xs: 14, sm: 15 },
                      fontWeight: 600,
                      fontFamily: 'TikTok Sans, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
                      lineHeight: '1.2',
                    },
                  }}
                />
              </Box>

              <Button
                fullWidth
                variant="contained"
                onClick={handleUpdateChildSubmit}
                disabled={isSubmitting || !childFormData.name || !isValidDateFormat(childFormData.birthDate)}
                sx={{
                  bgcolor: '#1976FF',
                  color: '#FFFFFF',
                  textTransform: 'none',
                  borderRadius: '24px',
                  height: { xs: 44, sm: 48 },
                  fontSize: { xs: 14, sm: 15 },
                  fontWeight: 600,
                  mt: 2,
                  '&:hover': { bgcolor: '#1565E0' },
                }}
              >
                {isSubmitting ? 'Сохранение...' : 'Сохранить'}
              </Button>
            </Box>
          </DialogContent>
        </Dialog>
      )}

      {/* Edit Pet Dialog */}
      {features.pets && (
        <Dialog
          open={openDialog === 'editPet'}
          onClose={() => {
            setOpenDialog(null);
            setEditingPetId(null);
          }}
          maxWidth="sm"
          fullWidth
          PaperProps={{
            sx: {
              borderRadius: '20px',
              maxHeight: '90vh',
            },
          }}
        >
          <DialogContent sx={{ p: 2 }}>
            {/* Header */}
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
              <Typography sx={{ fontSize: { xs: 15, sm: 16 }, fontWeight: 600, color: '#1C1C1E' }}>
                Изменить данные питомца
              </Typography>
              <IconButton
                size="small"
                onClick={() => {
                  setOpenDialog(null);
                  setEditingPetId(null);
                }}
              >
                <CloseIcon />
              </IconButton>
            </Box>

            {/* Form Fields */}
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
              <Box>
                <InputLabel
                  sx={{
                    color: '#007AFF',
                    fontSize: '14px',
                    fontWeight: 600,
                    mb: 0.75,
                    fontFamily: 'TikTok Sans, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
                    lineHeight: 1,
                  }}
                >
                  Введите имя питомца
                </InputLabel>
                <TextField
                  fullWidth
                  value={petFormData.name}
                  onChange={(e) =>
                    setPetFormData({ ...petFormData, name: e.target.value })
                  }
                  variant="outlined"
                  size="small"
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: '16px',
                      height: '44px',
                      bgcolor: '#FFFFFF',
                      '& fieldset': {
                        borderColor: '#212121',
                        borderWidth: '1.5px',
                      },
                      '&:hover fieldset': {
                        borderColor: '#212121',
                      },
                      '&.Mui-focused fieldset': {
                        borderColor: '#007AFF',
                        borderWidth: '1.5px',
                      },
                    },
                    '& .MuiOutlinedInput-input': {
                      fontSize: { xs: 14, sm: 15 },
                      fontWeight: 600,
                      fontFamily: 'TikTok Sans, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
                      lineHeight: '1.2',
                    },
                  }}
                />
              </Box>

              <Box>
                <InputLabel
                  sx={{
                    color: '#007AFF',
                    fontSize: '14px',
                    fontWeight: 600,
                    mb: 0.75,
                    fontFamily: 'TikTok Sans, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
                    lineHeight: 1,
                  }}
                >
                  Дата рождения питомца
                </InputLabel>
                <TextField
                  fullWidth
                  value={petFormData.birthDate}
                  onChange={(e) =>
                    setPetFormData({
                      ...petFormData,
                      birthDate: formatDateInput(e.target.value),
                    })
                  }
                  variant="outlined"
                  size="small"
                  placeholder="DD.MM.YYYY"
                  inputProps={{
                    maxLength: 10,
                  }}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: '16px',
                      height: '44px',
                      bgcolor: '#FFFFFF',
                      '& fieldset': {
                        borderColor: '#212121',
                        borderWidth: '1.5px',
                      },
                      '&:hover fieldset': {
                        borderColor: '#212121',
                      },
                      '&.Mui-focused fieldset': {
                        borderColor: '#007AFF',
                        borderWidth: '1.5px',
                      },
                    },
                    '& .MuiOutlinedInput-input': {
                      fontSize: { xs: 14, sm: 15 },
                      fontWeight: 600,
                      fontFamily: 'TikTok Sans, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
                      lineHeight: '1.2',
                    },
                  }}
                />
              </Box>

              <Box>
                <InputLabel
                  sx={{
                    color: '#007AFF',
                    fontSize: '14px',
                    fontWeight: 600,
                    mb: 0.75,
                    fontFamily: 'TikTok Sans, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
                    lineHeight: 1,
                  }}
                >
                  Порода
                </InputLabel>
                <TextField
                  fullWidth
                  value={petFormData.breed}
                  onChange={(e) =>
                    setPetFormData({ ...petFormData, breed: e.target.value })
                  }
                  variant="outlined"
                  size="small"
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: '16px',
                      height: '44px',
                      bgcolor: '#FFFFFF',
                      '& fieldset': {
                        borderColor: '#212121',
                        borderWidth: '1.5px',
                      },
                      '&:hover fieldset': {
                        borderColor: '#212121',
                      },
                      '&.Mui-focused fieldset': {
                        borderColor: '#007AFF',
                        borderWidth: '1.5px',
                      },
                    },
                    '& .MuiOutlinedInput-input': {
                      fontSize: { xs: 14, sm: 15 },
                      fontWeight: 600,
                      fontFamily: 'TikTok Sans, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
                      lineHeight: '1.2',
                    },
                  }}
                />
              </Box>

              <Button
                fullWidth
                variant="contained"
                onClick={handleUpdatePetSubmit}
                disabled={isSubmitting || !petFormData.name || !isValidDateFormat(petFormData.birthDate)}
                sx={{
                  bgcolor: '#1976FF',
                  color: '#FFFFFF',
                  textTransform: 'none',
                  borderRadius: '24px',
                  height: { xs: 44, sm: 48 },
                  fontSize: { xs: 14, sm: 15 },
                  fontWeight: 600,
                  mt: 2,
                  '&:hover': { bgcolor: '#1565E0' },
                }}
              >
                {isSubmitting ? 'Сохранение...' : 'Сохранить'}
              </Button>
            </Box>
          </DialogContent>
        </Dialog>
      )}

      {/* Add Pet Dialog */}
      {features.pets && (
        <Dialog
          open={openDialog === 'addPet'}
          onClose={() => setOpenDialog(null)}
          maxWidth="sm"
          fullWidth
          PaperProps={{
            sx: {
              borderRadius: '20px',
              maxHeight: '90vh',
            },
          }}
        >
          <DialogContent sx={{ p: 2 }}>
            {/* Header */}
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
              <Typography sx={{ fontSize: { xs: 15, sm: 16 }, fontWeight: 600, color: '#1C1C1E' }}>
                Добавить питомца
              </Typography>
              <IconButton size="small" onClick={() => setOpenDialog(null)}>
                <CloseIcon />
              </IconButton>
            </Box>

            {/* Form Fields */}
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
              <Box>
                <InputLabel
                  sx={{
                    color: '#007AFF',
                    fontSize: '14px',
                    fontWeight: 600,
                    mb: 0.75,
                    fontFamily: 'TikTok Sans, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
                    lineHeight: 1,
                  }}
                >
                  Введите имя питомца
                </InputLabel>
                <TextField
                  fullWidth
                  value={petFormData.name}
                  onChange={(e) =>
                    setPetFormData({ ...petFormData, name: e.target.value })
                  }
                  variant="outlined"
                  size="small"
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: '16px',
                      height: '44px',
                      bgcolor: '#FFFFFF',
                      '& fieldset': {
                        borderColor: '#212121',
                        borderWidth: '1.5px',
                      },
                      '&:hover fieldset': {
                        borderColor: '#212121',
                      },
                      '&.Mui-focused fieldset': {
                        borderColor: '#007AFF',
                        borderWidth: '1.5px',
                      },
                    },
                    '& .MuiOutlinedInput-input': {
                      fontSize: { xs: 14, sm: 15 },
                      fontWeight: 600,
                      fontFamily: 'TikTok Sans, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
                      lineHeight: '1.2',
                    },
                  }}
                />
              </Box>

              <Box>
                <InputLabel
                  sx={{
                    color: '#007AFF',
                    fontSize: '14px',
                    fontWeight: 600,
                    mb: 0.75,
                    fontFamily: 'TikTok Sans, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
                    lineHeight: 1,
                  }}
                >
                  Дата рождения питомца
                </InputLabel>
                <TextField
                  fullWidth
                  value={petFormData.birthDate}
                  onChange={(e) =>
                    setPetFormData({
                      ...petFormData,
                      birthDate: formatDateInput(e.target.value),
                    })
                  }
                  variant="outlined"
                  size="small"
                  placeholder="DD.MM.YYYY"
                  inputProps={{
                    maxLength: 10,
                  }}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: '16px',
                      height: '44px',
                      bgcolor: '#FFFFFF',
                      '& fieldset': {
                        borderColor: '#212121',
                        borderWidth: '1.5px',
                      },
                      '&:hover fieldset': {
                        borderColor: '#212121',
                      },
                      '&.Mui-focused fieldset': {
                        borderColor: '#007AFF',
                        borderWidth: '1.5px',
                      },
                    },
                    '& .MuiOutlinedInput-input': {
                      fontSize: { xs: 14, sm: 15 },
                      fontWeight: 600,
                      fontFamily: 'TikTok Sans, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
                      lineHeight: '1.2',
                    },
                  }}
                />
              </Box>

              <Box>
                <InputLabel
                  sx={{
                    color: '#1976FF',
                    fontSize: { xs: 12, sm: 13 },
                    fontWeight: 600,
                    mb: 0.75,
                  }}
                >
                  Порода питомца
                </InputLabel>
                <TextField
                  fullWidth
                  value={petFormData.breed}
                  onChange={(e) =>
                    setPetFormData({ ...petFormData, breed: e.target.value })
                  }
                  variant="outlined"
                  size="small"
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: '12px',
                      height: '44px',
                      bgcolor: '#F9F9F9',
                    },
                  }}
                />
              </Box>

              <Button
                fullWidth
                variant="contained"
                onClick={handleAddPetSubmit}
                disabled={isSubmitting || !petFormData.name || !isValidDateFormat(petFormData.birthDate)}
                sx={{
                  bgcolor: '#1976FF',
                  color: '#FFFFFF',
                  textTransform: 'none',
                  borderRadius: '24px',
                  height: { xs: 44, sm: 48 },
                  fontSize: { xs: 14, sm: 15 },
                  fontWeight: 600,
                  mt: 2,
                  '&:hover': { bgcolor: '#1565E0' },
                }}
              >
                {isSubmitting ? 'Добавление...' : 'Добавить'}
              </Button>
            </Box>
          </DialogContent>
        </Dialog>
      )}
    </Box>
  );
};
